import { getAuthToken } from './authStorage';
import { API_BASE_URL } from './apiBaseUrl';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: {
    message?: string;
  };
}

export type PreferredTimeOfDay = 'morning' | 'afternoon' | 'evening';

export interface SuggestionRequestPayload {
  clientId: string;
  preferredDuration: number;
  earliestDate: string;
  latestDate: string;
  workdayStartTime?: string;
  workdayEndTime?: string;
  enableMiddayBreak?: boolean;
  breakStartTime?: string;
  breakEndTime?: string;
  professionalBase?: {
    lat: number;
    lng: number;
  };
  preferredTimeOfDay?: PreferredTimeOfDay;
  excludeWeekends?: boolean;
  includeBuffer?: boolean;
  returnToBaseForMiddayBreak?: boolean;
  maxIdleTimeMinutes?: number;
  slotStepMinutes?: number;
  timeConstraints?: Array<{ start: string; end: string }>;
}

export interface SuggestedSlot {
  startTime: string;
  endTime: string;
  duration: number;
  optimizationScore?: number;
  travelTimeFromPrevious?: number;
  travelTimeToNext?: number;
  notes?: string;
}

export interface SuggestionResultPayload {
  suggestedSlots: SuggestedSlot[];
  alternativeSlots: SuggestedSlot[];
  confidence: number;
  explanation: string;
  optimizationMetrics: {
    totalTravelTime: number;
    totalDistance: number;
    numberOfClients: number;
    efficiencyScore: number;
    geographicalSpread: number;
    timeSpread: number;
  };
}

const extractApiErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = (await response.json()) as ApiResponse<unknown>;
    return payload.error?.message || payload.message || `Erreur HTTP: ${response.status}`;
  } catch {
    return `Erreur HTTP: ${response.status}`;
  }
};

const buildAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export class SuggestionApiService {
  static async getOptimalSlot(
    payload: SuggestionRequestPayload,
  ): Promise<SuggestionResultPayload> {
    const response = await fetch(`${API_BASE_URL}/suggestions/optimal-slot`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    const result = (await response.json()) as ApiResponse<SuggestionResultPayload>;
    if (!result.success || !result.data) {
      throw new Error(result.error?.message || result.message || 'Erreur lors de la suggestion de creneaux');
    }

    return result.data;
  }
}

export default SuggestionApiService;
